import {
  List as MuiList,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  type SxProps,
  type Theme,
} from '@mui/material';
import React from 'react';

import Icon from '@components/Icon/Icon';

export interface ListItemProps {
  /** Should this list item be displayed? */
  display?: boolean;
  /** Is this list item disabled? */
  disabled?: boolean;
  /** Callback function when the list item is clicked */
  handleClick?: React.MouseEventHandler<HTMLDivElement>;
  /** Is this list item selected? */
  selected?: boolean;
  /** Title */
  title: React.ReactNode;
  /** Subtitle */
  subtitle?: React.ReactNode;
  /** Action on the left side of list item */
  startingAction?: React.ReactNode;
  /** Action on the right side of list item (defaulted to right arrow) */
  endingAction?: React.ReactNode;
}

export interface ListSection {
  /** Section header label */
  header: React.ReactNode;
  /** Items within this section */
  items: ListItemProps[];
}

interface BaseListProps {
  /** Type of list (used for key) */
  type: string;
  /** Style props for the ListItemButton component */
  buttonProps?: SxProps<Theme>;
  /** Style props for the Paper component */
  paperProps?: SxProps<Theme>;
}

interface FlatListProps extends BaseListProps {
  items: ListItemProps[];
  sections?: never;
  maxHeight?: never;
}

interface SectionedListProps extends BaseListProps {
  items?: never;
  /** Grouped items with sticky subheaders */
  sections: ListSection[];
  /** Max height before scrolling (enables sticky headers) */
  maxHeight?: number | string;
}

export type ListProps = FlatListProps | SectionedListProps;

export default function List({
  buttonProps,
  items,
  maxHeight,
  paperProps,
  sections,
  type,
}: ListProps) {
  const defaultEndingAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );

  const filterItems = (list: ListItemProps[]) =>
    list.filter(({ display }) =>
      typeof display === 'boolean' ? display : true
    );

  const renderItem = (
    item: ListItemProps,
    index: number,
    totalItems: number
  ) => {
    const {
      disabled,
      endingAction,
      handleClick,
      selected,
      startingAction,
      subtitle,
      title,
    } = item;

    return (
      <ListItemButton
        key={`${type}-${index}`}
        disabled={typeof disabled === 'boolean' ? disabled : false}
        divider={index !== totalItems - 1}
        onClick={handleClick}
        selected={selected}
        sx={{
          '&:hover': {
            backgroundColor: ({ palette }) => {
              const defaultBackgroundColor =
                buttonProps && 'backgroundColor' in buttonProps
                  ? (buttonProps['backgroundColor'] as string)
                  : palette.background.paper;
              return handleClick ? palette.grey[200] : defaultBackgroundColor;
            },
            cursor: handleClick ? 'pointer' : 'auto',
          },
          ...buttonProps,
        }}
      >
        {startingAction && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 1,
            }}
          >
            {startingAction}
          </ListItemIcon>
        )}
        <ListItemText primary={title} secondary={subtitle} />
        {endingAction ?? defaultEndingAction}
      </ListItemButton>
    );
  };

  if (sections) {
    const filteredSections = sections.map((section) => ({
      ...section,
      items: filterItems(section.items),
    }));

    return (
      <Paper sx={{ ...paperProps }}>
        <MuiList
          subheader={<li />}
          sx={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: maxHeight ?? 300,
            '& ul': { padding: 0 },
          }}
        >
          {filteredSections.map((section, sectionIndex) => (
            <li key={`${type}-section-${sectionIndex}`}>
              <ul>
                <ListSubheader
                  sx={({ palette }) => ({
                    lineHeight: '32px',
                    py: 0,
                    backgroundColor: palette.grey[100],
                    zIndex: 10,
                  })}
                >
                  {section.header}
                </ListSubheader>
                {section.items.map((item, itemIndex) =>
                  renderItem(item, itemIndex, section.items.length)
                )}
              </ul>
            </li>
          ))}
        </MuiList>
      </Paper>
    );
  }

  const filteredItems = filterItems(items);

  return (
    <Paper sx={{ ...paperProps }}>
      <MuiList>
        {filteredItems.map((item, index) =>
          renderItem(item, index, filteredItems.length)
        )}
      </MuiList>
    </Paper>
  );
}
